// src/components/invoices/InvoiceList.js
import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
  Paper,
  Stack,
  useTheme,
  TablePagination,
  useMediaQuery,
  MenuItem,
} from "@mui/material";
import {
  Edit,
  Delete,
  Search,
  PictureAsPdf,
  Refresh,
  CheckCircle,
  Visibility,
  Description,
  ReceiptLong,
  Download,
  PlaylistAdd,
  SubdirectoryArrowRight,
} from "@mui/icons-material";
import {
  generateInvoicePDF,
  generateSelfInvoicePDF,
} from "../../services/pdfService";
import { useCollection } from "../../hooks/useCollection";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import { sendInvoiceEmail } from "../../services/emailService";
import {
  INVOICE_TYPE,
  INVOICE_TYPE_LABELS,
} from "../../utils/models/invoiceModel";
import {
  createPurchaseInvoice,
  EMITTER_TYPES,
} from "../../utils/models/purchaseInvoiceModel";
import LoadingSpinner from "../ui/LoadingSpinner";
import EmptyState from "../ui/EmptyState";
import ConfirmDialog from "../ui/ConfirmDialog";

const statusColors = {
  draft: "default",
  sent: "info",
  paid: "success",
  overdue: "error",
  cancelled: "error",
};
const statusLabels = {
  draft: "Borrador",
  sent: "Enviada",
  paid: "Pagada",
  overdue: "Vencida",
  cancelled: "Cancelada",
};

const formatInvoiceNumberDisplay = (invoice) => {
  const raw = invoice?.invoiceNumber;
  if (!raw || raw === "BORRADOR") return "BORRADOR";

  if (
    String(raw).startsWith("INV/") ||
    String(raw).startsWith("PROFORMA/") ||
    String(raw).startsWith("ABN/") ||
    String(raw).startsWith("LIQ/")
  ) {
    return raw;
  }

  const type = invoice.invoiceType;
  let prefix = "INV";
  if (type === INVOICE_TYPE.PROFORMA) prefix = "PROFORMA";
  else if (type === INVOICE_TYPE.ABONO) prefix = "ABN";
  else if (type === INVOICE_TYPE.LIQUIDACION) prefix = "LIQ";

  const year = invoice.issueDate
    ? new Date(invoice.issueDate).getFullYear()
    : new Date().getFullYear();

  const digitsMatch = String(raw).match(/(\d+)\s*$/);
  const number = digitsMatch
    ? digitsMatch[1].padStart(4, "0")
    : String(raw).padStart(4, "0");

  return `${prefix}/${year}/${number}`;
};

const formatInvoiceNumberWithFraction = (invoice) => {
  const base = formatInvoiceNumberDisplay(invoice);
  if (
    invoice?.invoiceType === INVOICE_TYPE.PROFORMA &&
    invoice?.fraction &&
    !String(base).endsWith(`-${invoice.fraction}`)
  ) {
    return `${base}-${invoice.fraction}`;
  }
  return base;
};

const extractRawNumber = (invoiceNumber) => {
  if (!invoiceNumber) return "";
  const str = String(invoiceNumber);
  const withoutPrefix = str.replace(/^[A-Z]+\//i, "");
  const withoutYear = withoutPrefix.replace(/^\d{4}\//, "");
  return withoutYear;
};

const getNextFraction = (existingFractions) => {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  const used = new Set(existingFractions.filter(Boolean));
  for (const letter of alphabet) {
    if (!used.has(letter)) return letter;
  }
  return null;
};

const InvoiceList = ({ onEdit, onView, onAddFraction, compact = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: invoices, loading } = useCollection("invoices");
  const { data: projects } = useCollection("projects");
  const [localInvoices, setLocalInvoices] = useState(null);
  const { remove, update } = useAuditFirestore("invoices");
  const { add: addInvoice } = useAuditFirestore("invoices");
  const { add: addSalesInvoice } = useAuditFirestore("salesInvoices");
  const { add: addPurchaseInvoice } = useAuditFirestore("purchaseInvoices");
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteGroupTarget, setDeleteGroupTarget] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(7);
  const [typeFilter, setTypeFilter] = useState("all");

  React.useEffect(() => {
    if (invoices) setLocalInvoices(invoices);
  }, [invoices]);

  React.useEffect(() => {
    setPage(0);
  }, [search, typeFilter]);

  const filtered = useMemo(() => {
    if (!localInvoices) return [];
    const searchLower = search.toLowerCase();

    let result = localInvoices.filter((i) => {
      const projectIds = (i.lines || [])
        .filter((l) => l.projectId)
        .map((l) => l.projectId);
      const projectNames = projectIds
        .map(
          (id) =>
            projects?.find((proj) => proj.id === id)?.name ||
            projects?.find((proj) => proj.id === id)?.title ||
            "",
        )
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const rawNumber = (i.invoiceNumber || "").toLowerCase();
      const formattedNumber = formatInvoiceNumberWithFraction(i).toLowerCase();

      const matchesSearch =
        search === "" ||
        rawNumber.includes(searchLower) ||
        formattedNumber.includes(searchLower) ||
        i.clientName?.toLowerCase().includes(searchLower) ||
        i.commercialName?.toLowerCase().includes(searchLower) ||
        projectNames.includes(searchLower);

      const matchesType =
        typeFilter === "all" ||
        i.invoiceType === typeFilter ||
        (!i.invoiceType && typeFilter === "normal");

      return i.isActive !== false && matchesSearch && matchesType;
    });

    result.sort((a, b) => {
      const dateA = a.issueDate ? new Date(a.issueDate) : new Date(0);
      const dateB = b.issueDate ? new Date(b.issueDate) : new Date(0);
      return dateB - dateA;
    });

    return result;
  }, [localInvoices, projects, search, typeFilter]);

  const groupedInvoices = useMemo(() => {
    const groups = [];
    let currentQuarter = null;

    const proformaGroupQuarter = new Map();
    filtered.forEach((invoice) => {
      if (
        invoice.invoiceType === INVOICE_TYPE.PROFORMA &&
        invoice.proformaNumber
      ) {
        if (!proformaGroupQuarter.has(invoice.proformaNumber)) {
          const date = invoice.issueDate
            ? new Date(invoice.issueDate)
            : new Date();
          const year = date.getFullYear();
          const quarter = Math.floor(date.getMonth() / 3) + 1;
          proformaGroupQuarter.set(
            invoice.proformaNumber,
            `Q${quarter} ${year}`,
          );
        }
      }
    });

    filtered.forEach((invoice) => {
      const isGroupedProforma =
        invoice.invoiceType === INVOICE_TYPE.PROFORMA &&
        !!invoice.proformaNumber;

      const date = invoice.issueDate ? new Date(invoice.issueDate) : new Date();
      const year = date.getFullYear();
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      let groupKey = `Q${quarter} ${year}`;

      if (isGroupedProforma) {
        groupKey = proformaGroupQuarter.get(invoice.proformaNumber) || groupKey;
      }

      const groupLabel = getQuarterLabelFromKey(groupKey);

      if (!currentQuarter || currentQuarter.key !== groupKey) {
        currentQuarter = {
          key: groupKey,
          label: groupLabel,
          subGroups: [],
        };
        groups.push(currentQuarter);
      }

      let subKey;
      let subType = "item";
      let subProformaNumber = null;

      if (isGroupedProforma) {
        subKey = `PROFORMA:${invoice.proformaNumber}`;
        subType = "proformaGroup";
        subProformaNumber = invoice.proformaNumber;
      } else {
        subKey = `ITEM:${invoice.id}`;
      }

      let subGroup = currentQuarter.subGroups.find((s) => s.key === subKey);
      if (!subGroup) {
        subGroup = {
          key: subKey,
          type: subType,
          proformaNumber: subProformaNumber,
          invoices: [],
        };
        currentQuarter.subGroups.push(subGroup);
      }
      subGroup.invoices.push(invoice);
    });

    groups.forEach((g) => {
      g.subGroups.forEach((sg) => {
        sg.invoices.sort((a, b) => {
          const fa = a.fraction || "";
          const fb = b.fraction || "";
          if (fa === fb) {
            const da = a.issueDate ? new Date(a.issueDate) : new Date(0);
            const db = b.issueDate ? new Date(b.issueDate) : new Date(0);
            return db - da;
          }
          return fa.localeCompare(fb);
        });
      });
    });

    return groups;
  }, [filtered]);

  const flatRows = useMemo(() => {
    const rows = [];
    groupedInvoices.forEach((g) => {
      rows.push({ type: "quarter", label: g.label, key: g.key });

      g.subGroups.forEach((sg) => {
        if (sg.type === "proformaGroup") {
          const total = sg.invoices.reduce(
            (sum, inv) => sum + (parseFloat(inv.total) || 0),
            0,
          );
          const anyConverted = sg.invoices.some(
            (i) => !!i.convertedToInvoiceId,
          );
          rows.push({
            type: "proformaHeader",
            key: sg.key,
            proformaNumber: sg.proformaNumber,
            invoices: sg.invoices,
            total,
            anyConverted,
          });
          sg.invoices.forEach((inv) => {
            rows.push({
              type: "fractionRow",
              key: `frac-${inv.id}`,
              invoice: inv,
            });
          });
        } else {
          sg.invoices.forEach((inv) => {
            rows.push({
              type: "invoice",
              key: `inv-${inv.id}`,
              invoice: inv,
            });
          });
        }
      });
    });
    return rows;
  }, [groupedInvoices]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return flatRows.slice(start, start + rowsPerPage);
  }, [flatRows, page, rowsPerPage]);

  const totalInvoices = filtered.length;

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDownloadPDF = async (invoice) => {
    await generateInvoicePDF(invoice);
  };

  const handleDownloadSelfInvoicePDF = async (invoice, e) => {
    e.stopPropagation();
    if (invoice.invoiceType !== INVOICE_TYPE.LIQUIDACION) return;
    try {
      await generateSelfInvoicePDF(invoice);
    } catch (error) {
      console.error("Error al descargar autofactura:", error);
      alert("Error al descargar la autofactura. Revisa la consola.");
    }
  };

  const handleAddFraction = async (headerRow, e) => {
    e.stopPropagation();

    const proformaNumber = headerRow.proformaNumber;
    const invoices = headerRow.invoices;

    const existingFractions = invoices.map((i) => i.fraction || "");
    const nextFraction = getNextFraction(existingFractions);

    if (!nextFraction) {
      alert("No quedan letras de fracción disponibles.");
      return;
    }

    const baseInvoice = invoices[0];

    if (typeof onAddFraction === "function") {
      onAddFraction({
        proformaNumber,
        fraction: nextFraction,
        baseInvoice,
      });
      return;
    }

    const nowIso = new Date().toISOString();

    const fractionData = {
      invoiceType: INVOICE_TYPE.PROFORMA,
      invoiceNumber: baseInvoice.invoiceNumber || proformaNumber,
      proformaNumber,
      fraction: nextFraction,

      clientId: baseInvoice.clientId || "",
      clientName: baseInvoice.clientName || "",
      clientTaxId: baseInvoice.clientTaxId || "",
      clientEmail: baseInvoice.clientEmail || "",
      clientAddress: baseInvoice.clientAddress || "",

      issuerName:
        baseInvoice.issuerName || "EPC Development & Management, S.L.",
      issuerTaxId: baseInvoice.issuerTaxId || "B26623850",
      issuerAddress:
        baseInvoice.issuerAddress || "C/ Juan de Austria 13, Cox, Alicante",

      lines: [],
      baseAmount: 0,
      taxRate: baseInvoice.taxRate ?? 21,
      taxAmount: 0,
      total: 0,

      issueDate: baseInvoice.issueDate || nowIso,
      dueDate: baseInvoice.dueDate || "",
      paymentMethod: baseInvoice.paymentMethod || "transfer",
      iban: baseInvoice.iban || "",
      status: "unpaid",
      notes: baseInvoice.notes || "",

      salesInvoiceId: null,
      purchaseInvoiceId: null,
      convertedToInvoiceId: null,
      convertedAt: null,
      relatedInvoiceId: null,
      relatedInvoiceNumber: null,

      createdAt: nowIso,
      updatedAt: nowIso,
      createdBy: "",
      isActive: true,
    };

    try {
      await addInvoice(fractionData);
    } catch (error) {
      console.error("Error al añadir fracción:", error);
      alert("Error al añadir fracción. Revisa la consola.");
    }
  };

  const handleConvertProformaGroupToInvoice = async (headerRow, e) => {
    e.stopPropagation();

    const invoicesInGroup = headerRow.invoices;
    if (!invoicesInGroup || invoicesInGroup.length === 0) return;

    const base = invoicesInGroup[0];

    const rawNumber = extractRawNumber(base.invoiceNumber);
    const newInvoiceNumber = (rawNumber || "").trim();
    if (!newInvoiceNumber) {
      alert("La proforma no tiene número. Edítala primero.");
      return;
    }

    const allLines = invoicesInGroup.flatMap((inv) =>
      (inv.lines || []).map((l) => ({ ...l })),
    );

    const baseAmount = allLines.reduce(
      (sum, l) => sum + (parseFloat(l.total) || 0),
      0,
    );
    const taxAmount = allLines.reduce(
      (sum, l) => sum + (parseFloat(l.taxAmount) || 0),
      0,
    );
    const total = baseAmount + taxAmount;
    const avgTaxRate = baseAmount > 0 ? (taxAmount / baseAmount) * 100 : 21;

    try {
      const nowIso = new Date().toISOString();

      const newInvoiceData = {
        invoiceType: INVOICE_TYPE.NORMAL,
        invoiceNumber: newInvoiceNumber,
        proformaNumber: "",
        fraction: "",

        clientId: base.clientId || "",
        clientName: base.clientName || "",
        clientTaxId: base.clientTaxId || "",
        clientEmail: base.clientEmail || "",
        clientAddress: base.clientAddress || "",

        issuerName: base.issuerName || "EPC Development & Management, S.L.",
        issuerTaxId: base.issuerTaxId || "B26623850",
        issuerAddress:
          base.issuerAddress || "C/ Juan de Austria 13, Cox, Alicante",

        lines: allLines,
        baseAmount,
        taxRate: Math.round(avgTaxRate * 100) / 100,
        taxAmount,
        total,

        issueDate: nowIso,
        dueDate: base.dueDate || "",
        paymentMethod: base.paymentMethod || "transfer",
        iban: base.iban || "",
        status: "unpaid",
        notes: base.notes || "",

        convertedFromInvoiceId: invoicesInGroup.map((i) => i.id),
        convertedFromInvoiceType: INVOICE_TYPE.PROFORMA,
        conversionGroupId: headerRow.key,

        salesInvoiceId: null,
        purchaseInvoiceId: null,
        convertedToInvoiceId: null,
        convertedAt: null,

        sentAt: null,
        paidAt: null,
        createdAt: nowIso,
        updatedAt: nowIso,
        createdBy: "",
        isActive: true,
      };

      const savedNewInvoice = await addInvoice(newInvoiceData);
      const newInvoiceId = savedNewInvoice?.id;

      const salesInvoiceData = {
        invoiceNumber: newInvoiceNumber,
        clientId: base.clientId,
        clientName: base.clientName,
        clientTaxId: base.clientTaxId,
        clientEmail: base.clientEmail,
        clientAddress: base.clientAddress,
        issuerName: base.issuerName || "EPC Development & Management, S.L.",
        issuerTaxId: base.issuerTaxId || "B26623850",
        issuerAddress:
          base.issuerAddress || "C/ Juan de Austria 13, Cox, Alicante",
        baseAmount,
        taxRate: Math.round(avgTaxRate * 100) / 100,
        taxAmount,
        total,
        projects: allLines
          .filter((l) => l.projectId)
          .map((l) => ({
            projectId: l.projectId,
            amount: (parseFloat(l.total) || 0) + (parseFloat(l.taxAmount) || 0),
          })),
        issueDate: nowIso,
        dueDate: base.dueDate || "",
        paymentMethod: base.paymentMethod,
        iban: base.iban,
        status: "unpaid",
        notes: base.notes || "",
        createdAt: nowIso,
        createdBy: "",
        isActive: true,
      };

      const savedSalesInvoice = await addSalesInvoice(salesInvoiceData);

      if (newInvoiceId && savedSalesInvoice?.id) {
        await update(newInvoiceId, { salesInvoiceId: savedSalesInvoice.id });
      }

      await Promise.all(
        invoicesInGroup.map((inv) =>
          update(inv.id, {
            convertedToInvoiceId: newInvoiceId || null,
            convertedAt: nowIso,
            updatedAt: nowIso,
          }),
        ),
      );

      setLocalInvoices((prev) =>
        prev.map((inv) =>
          invoicesInGroup.some((g) => g.id === inv.id)
            ? {
                ...inv,
                convertedToInvoiceId: newInvoiceId || null,
                convertedAt: nowIso,
              }
            : inv,
        ),
      );
    } catch (error) {
      console.error("Error al convertir grupo a factura:", error);
      alert("Error al convertir el grupo en factura. Revisa la consola.");
    }
  };

  const handleConvertSingleProforma = async (invoice, e) => {
    e.stopPropagation();

    const rawNumber = extractRawNumber(invoice.invoiceNumber);
    const newInvoiceNumber = (rawNumber || "").trim();
    if (!newInvoiceNumber) {
      alert("La proforma no tiene número. Edítala primero.");
      return;
    }

    try {
      const nowIso = new Date().toISOString();

      const newInvoiceData = {
        invoiceType: INVOICE_TYPE.NORMAL,
        invoiceNumber: newInvoiceNumber,

        clientId: invoice.clientId || "",
        clientName: invoice.clientName || "",
        clientTaxId: invoice.clientTaxId || "",
        clientEmail: invoice.clientEmail || "",
        clientAddress: invoice.clientAddress || "",

        issuerName: invoice.issuerName || "EPC Development & Management, S.L.",
        issuerTaxId: invoice.issuerTaxId || "B26623850",
        issuerAddress:
          invoice.issuerAddress || "C/ Juan de Austria 13, Cox, Alicante",

        lines: invoice.lines || [],
        baseAmount: invoice.baseAmount || 0,
        taxRate: invoice.taxRate || 21,
        taxAmount: invoice.taxAmount || 0,
        total: invoice.total || 0,

        issueDate: nowIso,
        dueDate: invoice.dueDate || "",
        paymentMethod: invoice.paymentMethod || "transfer",
        iban: invoice.iban || "",
        status: "unpaid",
        notes: invoice.notes || "",

        convertedFromInvoiceId: invoice.id,
        convertedFromInvoiceType: invoice.invoiceType,

        salesInvoiceId: null,
        purchaseInvoiceId: null,
        convertedToInvoiceId: null,
        convertedAt: null,

        sentAt: null,
        paidAt: null,
        createdAt: nowIso,
        updatedAt: nowIso,
        createdBy: "",
        isActive: true,
      };

      const savedNewInvoice = await addInvoice(newInvoiceData);
      const newInvoiceId = savedNewInvoice?.id;

      const salesInvoiceData = {
        invoiceNumber: newInvoiceNumber,
        clientId: invoice.clientId,
        clientName: invoice.clientName,
        clientTaxId: invoice.clientTaxId,
        clientEmail: invoice.clientEmail,
        clientAddress: invoice.clientAddress,
        issuerName: invoice.issuerName || "EPC Development & Management, S.L.",
        issuerTaxId: invoice.issuerTaxId || "B26623850",
        issuerAddress:
          invoice.issuerAddress || "C/ Juan de Austria 13, Cox, Alicante",
        baseAmount: invoice.baseAmount,
        taxRate: invoice.taxRate,
        taxAmount: invoice.taxAmount,
        total: invoice.total,
        projects: (invoice.lines || [])
          .filter((l) => l.projectId)
          .map((l) => ({
            projectId: l.projectId,
            amount: (l.total || 0) + (l.taxAmount || 0),
          })),
        issueDate: nowIso,
        dueDate: invoice.dueDate || "",
        paymentMethod: invoice.paymentMethod,
        iban: invoice.iban,
        status: "unpaid",
        notes: invoice.notes || "",
        createdAt: nowIso,
        createdBy: "",
        isActive: true,
      };

      const savedSalesInvoice = await addSalesInvoice(salesInvoiceData);

      if (newInvoiceId && savedSalesInvoice?.id) {
        await update(newInvoiceId, { salesInvoiceId: savedSalesInvoice.id });
      }

      await update(invoice.id, {
        convertedToInvoiceId: newInvoiceId || null,
        convertedAt: nowIso,
        updatedAt: nowIso,
      });

      setLocalInvoices((prev) =>
        prev.map((inv) =>
          inv.id === invoice.id
            ? {
                ...inv,
                convertedToInvoiceId: newInvoiceId || null,
                convertedAt: nowIso,
              }
            : inv,
        ),
      );
    } catch (error) {
      console.error("Error al convertir en factura:", error);
      alert("Error al convertir en factura. Revisa la consola.");
    }
  };

  const handleConvertToSelfInvoice = async (invoice, e) => {
    e.stopPropagation();
    if (invoice.invoiceType !== INVOICE_TYPE.LIQUIDACION) return;

    try {
      await generateSelfInvoicePDF(invoice);
      const nowIso = new Date().toISOString();

      const purchaseInvoiceData = createPurchaseInvoice({
        invoiceNumber: extractRawNumber(invoice.invoiceNumber) || "",
        emitterType: EMITTER_TYPES.COMMERCIAL,
        emitterId: invoice.commercialId || "",
        emitterName: invoice.commercialName || "",
        emitterTaxId: invoice.commercialTaxId || "",
        emitterAddress: invoice.commercialAddress || "",
        emitterEmail: invoice.commercialEmail || "",
        receiverName:
          invoice.issuerName || "EPC Development & Management, S.L.",
        receiverTaxId: invoice.issuerTaxId || "B26623850",
        receiverAddress:
          invoice.issuerAddress || "C/ Juan de Austria 13, Cox, Alicante",
        baseAmount: invoice.baseAmount || 0,
        taxRate: 0,
        taxAmount: 0,
        total: invoice.baseAmount || 0,
        lines: (invoice.lines || []).map((l) => ({
          id: l.id,
          description:
            l.projectName || l.projectTitle || l.projectId || "Comisión",
          quantity: 1,
          unitPrice: l.commission || 0,
          taxRate: 0,
          total: l.commission || 0,
          taxAmount: 0,
          projectId: l.projectId || null,
          quarter: l.quarter || null,
        })),
        projects: (invoice.lines || [])
          .filter((l) => l.projectId)
          .map((l) => ({
            projectId: l.projectId,
            amount: parseFloat(l.commission) || 0,
          })),
        issueDate: invoice.issueDate
          ? new Date(invoice.issueDate).toISOString()
          : nowIso,
        dueDate: invoice.dueDate ? new Date(invoice.dueDate).toISOString() : "",
        paymentMethod: "transfer",
        iban: invoice.iban || "",
        status: "unpaid",
        notes:
          invoice.notes ||
          `Autofactura generada desde liquidación ${invoice.invoiceNumber || ""}`,
        relatedInvoiceId: invoice.id || null,
        relatedInvoiceNumber: invoice.invoiceNumber || null,
        createdAt: nowIso,
        createdBy: "",
        isActive: true,
      });

      delete purchaseInvoiceData.id;

      const savedPurchaseInvoice =
        await addPurchaseInvoice(purchaseInvoiceData);

      await update(invoice.id, {
        purchaseInvoiceId: savedPurchaseInvoice?.id || null,
        updatedAt: nowIso,
      });

      setLocalInvoices((prev) =>
        prev.map((inv) =>
          inv.id === invoice.id
            ? { ...inv, purchaseInvoiceId: savedPurchaseInvoice?.id || null }
            : inv,
        ),
      );
    } catch (error) {
      console.error("Error al convertir a autofactura:", error);
      alert("Error al convertir a autofactura. Revisa la consola.");
    }
  };

  const handleDeleteGroup = async () => {
    if (!deleteGroupTarget) return;

    const invoicesInGroup = deleteGroupTarget.invoices;

    setLocalInvoices((prev) =>
      prev.filter((i) => !invoicesInGroup.some((g) => g.id === i.id)),
    );
    setDeleteGroupTarget(null);

    try {
      await Promise.all(invoicesInGroup.map((inv) => remove(inv.id)));
    } catch (error) {
      console.error("Error deleting group:", error);
      setLocalInvoices((prev) => [...prev, ...invoicesInGroup]);
    }
  };

  if (loading || !localInvoices) return <LoadingSpinner />;

  return (
    <Box>
      {!compact && (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2 },
            mb: { xs: 2, sm: 3 },
            borderRadius: { xs: "16px", sm: "20px" },
            border: "1px solid",
            borderColor: theme.palette.divider,
            background: theme.palette.background.paper,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            gap={1.5}
            flexWrap="wrap"
          >
            <TextField
              placeholder="Buscar facturas..."
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                width: { xs: "100%", sm: 280, md: 360 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  height: 44,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search
                      fontSize="small"
                      sx={{ color: theme.palette.primary.main }}
                    />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch("")}>
                      ✖
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={`Total: ${totalInvoices}`}
                sx={{
                  height: 44,
                  borderRadius: "12px",
                  fontWeight: 700,
                  fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  border: "1px solid",
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                }}
              />
              <TextField
                select
                size="small"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                sx={{
                  width: 150,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    height: 44,
                    fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  },
                }}
              >
                <MenuItem value="all">Todas</MenuItem>
                <MenuItem value="normal">Facturas</MenuItem>
                <MenuItem value="proforma">Proformas</MenuItem>
                <MenuItem value="abono">Abonos</MenuItem>
                <MenuItem value="liquidacion">Liquidaciones</MenuItem>
              </TextField>
              <Tooltip title="Refrescar">
                <IconButton
                  onClick={() => {
                    if (invoices) setLocalInvoices(invoices);
                  }}
                  size="small"
                  sx={{ color: theme.palette.primary.main }}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
          <Typography
            variant="caption"
            sx={{
              mt: 1,
              display: "block",
              color: theme.palette.text.secondary,
              fontSize: "0.7rem",
              fontStyle: "italic",
            }}
          >
            Puedes buscar por nombre de proyecto, número de factura, cliente o
            comercial
          </Typography>
        </Paper>
      )}

      {filtered?.length === 0 ? (
        <EmptyState
          title={search ? "Sin resultados" : "Sin facturas"}
          description={
            search
              ? "Prueba con otros términos"
              : "Crea tu primera factura de venta"
          }
        />
      ) : (
        <Card
          elevation={2}
          sx={{
            borderRadius: { xs: 1, sm: 2 },
            overflow: "hidden",
            border: "1px solid",
            borderColor: theme.palette.divider,
            background: theme.palette.background.paper,
          }}
        >
          <TableContainer sx={{ overflowX: "auto", width: "100%" }}>
            <Table
              size={isMobile ? "small" : "medium"}
              sx={{
                width: "100%",
                minWidth: "100%",
                "& td:last-child:not(.quarter-cell), & th:last-child": {
                  textAlign: "right",
                },
                "& td:last-child:not(.quarter-cell) > *": {
                  justifyContent: "flex-end",
                },
              }}
            >
              {!compact && (
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(0, 175, 163, 0.1)"
                          : "rgba(0, 95, 101, 0.05)",
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "0.75rem", sm: "0.85rem" },
                      }}
                    >
                      Tipo
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "0.75rem", sm: "0.85rem" },
                      }}
                    >
                      Nº Factura
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "0.75rem", sm: "0.85rem" },
                      }}
                    >
                      Cliente / Comercial
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "0.75rem", sm: "0.85rem" },
                      }}
                    >
                      Fecha
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "0.75rem", sm: "0.85rem" },
                      }}
                    >
                      Total
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 600,
                        width: { xs: 170, sm: 300 },
                        fontSize: { xs: "0.75rem", sm: "0.85rem" },
                      }}
                    >
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
              )}
              <TableBody>
                {paginatedRows.map((row, idx) => {
                  // Fila trimestre
                  if (row.type === "quarter") {
                    return (
                      <TableRow key={`quarter-${row.key}-${idx}`}>
                        <TableCell
                          className="quarter-cell"
                          colSpan={compact ? 4 : 6}
                          sx={{
                            textAlign: "left !important",
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "rgba(0, 175, 163, 0.08)"
                                : "rgba(0, 95, 101, 0.03)",
                            py: 1.5,
                            borderBottom: `2px solid ${theme.palette.primary.main}`,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 700,
                              color: theme.palette.primary.main,
                              textAlign: "left",
                              fontSize: { xs: "0.85rem", sm: "0.95rem" },
                            }}
                          >
                            {row.label}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  // Cabecera del grupo de proformas — como una fila normal más
                  if (row.type === "proformaHeader") {
                    const base = row.invoices[0];
                    const year = base?.issueDate
                      ? new Date(base.issueDate).getFullYear()
                      : new Date().getFullYear();
                    const displayNumber = `Proforma/${year}/${String(
                      row.proformaNumber,
                    ).padStart(4, "0")}`;

                    const fractionCount = row.invoices.length;

                    const lilac = theme.palette.warning.main;
                    const lilacDark = theme.palette.warning.dark;

                    return (
                      <TableRow
                        key={`pfh-${row.key}`}
                        sx={{
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? `${lilac}22`
                              : `${lilac}18`,
                          borderLeft: `4px solid ${lilac}`,
                          borderTop: `1px solid ${lilac}66`,
                          borderBottom: `1px solid ${lilac}66`,
                        }}
                      >
                        {/* Col 1: Tipo */}
                        <TableCell
                          sx={{ py: 1.25, verticalAlign: "middle" }}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.75}
                            flexWrap="wrap"
                          >
                            <Chip
                              label="Proforma"
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.7rem",
                                backgroundColor: lilac,
                                color: "white",
                              }}
                            />
                            <Chip
                              label={String(fractionCount)}
                              size="small"
                              sx={{
                                backgroundColor: lilacDark,
                                color: "white",
                                fontWeight: 800,
                                fontSize: "0.7rem",
                                height: 22,
                                minWidth: 22,
                              }}
                            />
                          </Stack>
                        </TableCell>

                        {/* Col 2: Nº Factura */}
                        <TableCell
                          sx={{ py: 1.25, verticalAlign: "middle" }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 800,
                              color: lilacDark,
                              fontSize: { xs: "0.85rem", sm: "0.95rem" },
                            }}
                          >
                            {displayNumber}
                          </Typography>
                        </TableCell>

                        {/* Col 3: Cliente */}
                        <TableCell
                          sx={{ py: 1.25, verticalAlign: "middle" }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: lilacDark,
                              fontSize: { xs: "0.8rem", sm: "0.9rem" },
                            }}
                          >
                            {base?.clientName || "-"}
                          </Typography>
                        </TableCell>

                        {/* Col 4: Fecha */}
                        <TableCell
                          sx={{ py: 1.25, verticalAlign: "middle" }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: lilacDark,
                              fontSize: { xs: "0.75rem", sm: "0.85rem" },
                            }}
                          >
                            {base?.issueDate
                              ? new Date(base.issueDate).toLocaleDateString(
                                  "es-ES",
                                  {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                  },
                                )
                              : "-"}
                          </Typography>
                        </TableCell>

                        {/* Col 5: Total */}
                        <TableCell
                          sx={{ py: 1.25, verticalAlign: "middle" }}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.75}
                            flexWrap="wrap"
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 800,
                                color: lilacDark,
                                fontSize: { xs: "0.85rem", sm: "0.95rem" },
                              }}
                            >
                              {row.total.toLocaleString("es-ES", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                              €
                            </Typography>
                            {row.anyConverted && (
                              <Chip
                                label="Convertida"
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "0.6rem",
                                  height: 18,
                                  color: lilacDark,
                                  borderColor: lilac,
                                }}
                              />
                            )}
                          </Stack>
                        </TableCell>

                        {/* Col 6: Acciones — iconos a la derecha del todo */}
                        <TableCell
                          align="right"
                          sx={{
                            py: 1.25,
                            verticalAlign: "middle",
                            pr: { xs: 1, sm: 2 },
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                            justifyContent="flex-end"
                          >
                            <Tooltip title="Añadir fracción">
                              <IconButton
                                size="small"
                                onClick={(e) => handleAddFraction(row, e)}
                                sx={{
                                  color: lilac,
                                  borderRadius: "50%",
                                  p: 0.75,
                                  "&:hover": {
                                    backgroundColor:
                                      theme.palette.mode === "dark"
                                        ? `${lilac}33`
                                        : `${lilac}22`,
                                  },
                                }}
                              >
                                <PlaylistAdd
                                  sx={{ fontSize: { xs: 20, sm: 22 } }}
                                />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Convertir a factura">
                              <IconButton
                                size="small"
                                onClick={(e) =>
                                  handleConvertProformaGroupToInvoice(row, e)
                                }
                                sx={{
                                  color: lilac,
                                  borderRadius: "50%",
                                  p: 0.75,
                                  "&:hover": {
                                    backgroundColor:
                                      theme.palette.mode === "dark"
                                        ? `${lilac}33`
                                        : `${lilac}22`,
                                  },
                                }}
                              >
                                <Description
                                  sx={{ fontSize: { xs: 20, sm: 22 } }}
                                />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Eliminar proforma (y todas sus fracciones)">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteGroupTarget(row);
                                }}
                                sx={{
                                  color: theme.palette.error.main,
                                  borderRadius: "50%",
                                  p: 0.75,
                                  "&:hover": {
                                    backgroundColor:
                                      theme.palette.mode === "dark"
                                        ? "rgba(244,67,54,0.18)"
                                        : "rgba(244,67,54,0.10)",
                                  },
                                }}
                              >
                                <Delete
                                  sx={{ fontSize: { xs: 20, sm: 22 } }}
                                />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  // Fila de fracción
                  if (row.type === "fractionRow") {
                    const invoice = row.invoice;
                    const isConverted = !!invoice.convertedToInvoiceId;

                    return (
                      <TableRow
                        key={row.key}
                        hover
                        onClick={() => onView && onView(invoice)}
                        sx={{
                          cursor: onView ? "pointer" : "default",
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,152,0,0.03)"
                              : "rgba(255,152,0,0.02)",
                        }}
                      >
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <SubdirectoryArrowRight
                              sx={{
                                color: theme.palette.warning.main,
                                fontSize: 20,
                              }}
                            />
                            <Chip
                              label={`Fracción ${invoice.fraction || ""}`}
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{
                                fontWeight: 600,
                                fontSize: { xs: "0.65rem", sm: "0.7rem" },
                              }}
                            />
                            {isConverted && (
                              <Chip
                                label="Convertida"
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "0.6rem",
                                  height: 18,
                                }}
                              />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                              fontSize: { xs: "0.8rem", sm: "0.875rem" },
                              color: theme.palette.warning.dark,
                            }}
                          >
                            {formatInvoiceNumberWithFraction(invoice)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                          >
                            {invoice.clientName || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                          >
                            {invoice.issueDate
                              ? new Date(invoice.issueDate).toLocaleDateString(
                                  "es-ES",
                                  {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                  },
                                )
                              : "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            fontWeight={600}
                            sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                          >
                            {invoice.total?.toLocaleString("es-ES", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                            €
                          </Typography>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ pr: { xs: 1, sm: 2 } }}
                        >
                          <Stack
                            direction="row"
                            spacing={0.5}
                            justifyContent="flex-end"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {onView && (
                              <Tooltip title="Ver detalle">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onView(invoice);
                                  }}
                                  sx={{ color: theme.palette.info.main }}
                                >
                                  <Visibility
                                    sx={{ fontSize: { xs: 18, sm: 20 } }}
                                  />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Descargar PDF">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadPDF(invoice);
                                }}
                                sx={{ color: theme.palette.error.main }}
                              >
                                <PictureAsPdf
                                  sx={{ fontSize: { xs: 18, sm: 20 } }}
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEdit(invoice);
                                }}
                                sx={{ color: theme.palette.primary.main }}
                              >
                                <Edit sx={{ fontSize: { xs: 18, sm: 20 } }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar fracción">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteTarget(invoice);
                                }}
                                sx={{ color: theme.palette.error.main }}
                              >
                                <Delete sx={{ fontSize: { xs: 18, sm: 20 } }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  // Fila normal
                  if (row.type === "invoice") {
                    const invoice = row.invoice;
                    const isProforma =
                      invoice.invoiceType === INVOICE_TYPE.PROFORMA;
                    const isLiquidacion =
                      invoice.invoiceType === INVOICE_TYPE.LIQUIDACION;
                    const isAbono = invoice.invoiceType === INVOICE_TYPE.ABONO;
                    const isConverted = !!invoice.convertedToInvoiceId;

                    return (
                      <TableRow
                        key={row.key}
                        hover
                        onClick={() => onView && onView(invoice)}
                        sx={{
                          cursor: onView ? "pointer" : "default",
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                          >
                            <Chip
                              label={
                                isProforma
                                  ? "Proforma"
                                  : isLiquidacion
                                    ? "Liquidación"
                                    : isAbono
                                      ? "Abono"
                                      : "Factura"
                              }
                              size="small"
                              color={
                                isProforma
                                  ? "warning"
                                  : isLiquidacion
                                    ? "info"
                                    : isAbono
                                      ? "secondary"
                                      : "primary"
                              }
                              variant="outlined"
                              sx={{
                                fontWeight: 600,
                                fontSize: { xs: "0.7rem", sm: "0.75rem" },
                              }}
                            />
                            {isConverted && (
                              <Chip
                                label="Convertida"
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "0.65rem",
                                  height: 20,
                                }}
                              />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                          >
                            {formatInvoiceNumberWithFraction(invoice)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                          >
                            {isLiquidacion
                              ? invoice.commercialName || "-"
                              : invoice.clientName || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                          >
                            {invoice.issueDate
                              ? new Date(invoice.issueDate).toLocaleDateString(
                                  "es-ES",
                                  {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                  },
                                )
                              : "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            fontWeight={600}
                            sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                          >
                            {invoice.total?.toLocaleString("es-ES", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                            €
                          </Typography>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ pr: { xs: 1, sm: 2 } }}
                        >
                          <Stack
                            direction="row"
                            spacing={0.5}
                            justifyContent="flex-end"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {onView && (
                              <Tooltip title="Ver detalle">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onView(invoice);
                                  }}
                                  sx={{ color: theme.palette.info.main }}
                                >
                                  <Visibility
                                    sx={{ fontSize: { xs: 18, sm: 20 } }}
                                  />
                                </IconButton>
                              </Tooltip>
                            )}

                            {isProforma &&
                              !invoice.proformaNumber &&
                              !isConverted && (
                                <Tooltip title="Convertir en factura">
                                  <IconButton
                                    size="small"
                                    onClick={(e) =>
                                      handleConvertSingleProforma(invoice, e)
                                    }
                                    sx={{ color: theme.palette.success.main }}
                                  >
                                    <CheckCircle
                                      sx={{ fontSize: { xs: 18, sm: 20 } }}
                                    />
                                  </IconButton>
                                </Tooltip>
                              )}

                            {isLiquidacion && (
                              <>
                                <Tooltip title="Descargar autofactura (solo PDF)">
                                  <IconButton
                                    size="small"
                                    onClick={(e) =>
                                      handleDownloadSelfInvoicePDF(invoice, e)
                                    }
                                    sx={{ color: theme.palette.info.main }}
                                  >
                                    <Download
                                      sx={{ fontSize: { xs: 18, sm: 20 } }}
                                    />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Convertir a autofactura (PDF + factura de compra)">
                                  <IconButton
                                    size="small"
                                    onClick={(e) =>
                                      handleConvertToSelfInvoice(invoice, e)
                                    }
                                    sx={{
                                      color: theme.palette.secondary.main,
                                    }}
                                  >
                                    <ReceiptLong
                                      sx={{ fontSize: { xs: 18, sm: 20 } }}
                                    />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}

                            <Tooltip title="Descargar PDF">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadPDF(invoice);
                                }}
                                sx={{ color: theme.palette.error.main }}
                              >
                                <PictureAsPdf
                                  sx={{ fontSize: { xs: 18, sm: 20 } }}
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEdit(invoice);
                                }}
                                sx={{ color: theme.palette.primary.main }}
                              >
                                <Edit sx={{ fontSize: { xs: 18, sm: 20 } }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteTarget(invoice);
                                }}
                                sx={{ color: theme.palette.error.main }}
                              >
                                <Delete sx={{ fontSize: { xs: 18, sm: 20 } }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  return null;
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {!compact && (
            <TablePagination
              component="div"
              count={flatRows.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[7, 10, 25, 50]}
              labelRowsPerPage={isMobile ? "Filas:" : "Filas por página:"}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count}`
              }
              sx={{
                borderTop: "1px solid",
                borderColor: theme.palette.divider,
                "& .MuiTablePagination-toolbar": {
                  minHeight: 48,
                  px: { xs: 1, sm: 2 },
                  fontSize: { xs: "0.75rem", sm: "0.85rem" },
                },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                  {
                    fontSize: { xs: "0.7rem", sm: "0.85rem" },
                  },
              }}
            />
          )}
        </Card>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          setLocalInvoices((prev) =>
            prev.filter((i) => i.id !== deleteTarget.id),
          );
          const deletedInvoice = deleteTarget;
          setDeleteTarget(null);
          try {
            await remove(deletedInvoice.id);
          } catch (error) {
            console.error("Error deleting:", error);
            setLocalInvoices((prev) => [...prev, deletedInvoice]);
          }
        }}
        title="Eliminar Fracción"
        message={`¿Eliminar fracción ${deleteTarget?.invoiceNumber}?`}
        type="delete"
      />

      <ConfirmDialog
        open={!!deleteGroupTarget}
        onClose={() => setDeleteGroupTarget(null)}
        onConfirm={handleDeleteGroup}
        title="Eliminar Proforma completa"
        message={`¿Eliminar la proforma ${
          deleteGroupTarget?.proformaNumber || ""
        } y TODAS sus fracciones (${
          deleteGroupTarget?.invoices?.length || 0
        })?`}
        type="delete"
      />
    </Box>
  );
};

const getQuarterLabelFromKey = (key) => {
  const match = String(key).match(/^Q(\d)\s(\d{4})$/);
  if (!match) return key;
  const quarter = parseInt(match[1], 10);
  const year = match[2];
  const quarterLabels = {
    1: "1er Trimestre",
    2: "2do Trimestre",
    3: "3er Trimestre",
    4: "4to Trimestre",
  };
  return `${quarterLabels[quarter]} ${year}`;
};

export default InvoiceList;